import React from 'react';
import { CButton, CModal, CModalBody, CModalHeader, CModalTitle, CModalFooter } from '@coreui/react';
import _t from 'counterpart';
import { useMutation, useQueryClient } from 'react-query';
import { deleteSalesStatus } from '../../services/BackendService';
import { extractErrorMessage } from '../../helpers';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import { ISalesStatus } from './types';
import toast from 'react-hot-toast';

interface IProps {
  status: ISalesStatus;
  show: boolean;
  onClose: (refetch?: boolean) => void;
}

const DeleteSalesStatusModal = ({ status, show, onClose }: IProps) => {
  const queryClient = useQueryClient();

  const deleteSalesStatusMutation = useMutation((statusId: number) => deleteSalesStatus(statusId), {
    onSuccess: async () => {
      toast.success(_t('sales-status.deleted-successfully'));
      await queryClient.invalidateQueries('sales-statuses');
      onClose(true);
    },
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const loading = deleteSalesStatusMutation.isLoading;

  const deleteSalesStatusCall = () => {
    deleteSalesStatusMutation.mutate(status.id);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <CModal size="sm" show={show} onClose={handleClose} closeOnBackdrop={false}>
      <CModalHeader>
        <CModalTitle>{_t('sales-status.delete-modal-title', { status: status.status })}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <p>{_t('sales-status.delete-modal-description', { status: status.status })}</p>
        {deleteSalesStatusMutation.isError && (
          <span className="text-danger">{extractErrorMessage(deleteSalesStatusMutation.error)}</span>
        )}
      </CModalBody>
      <CModalFooter>
        <ButtonWithLoader
          isLoading={loading}
          onClick={deleteSalesStatusCall}
          buttonColor="danger"
          spinnerColor="secondary"
          title={_t.translate('global.confirm')}
          className="mr-2"
        />
        <CButton disabled={loading} color="light" variant="outline" onClick={handleClose}>
          {_t('global.cancel')}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default DeleteSalesStatusModal;
